/**
 * Pure validation functions for Playbook Definition forms.
 * Used by the Definition Catalog page for client-side validation
 * before submission.
 */

import type { PlaybookType } from '@/api/configuration';

// --- Constants ---

export const DEFINITION_NAME_MAX_LENGTH = 256;
export const DEFINITION_PATH_MAX_LENGTH = 512;
export const DEFINITION_DESCRIPTION_MAX_LENGTH = 1024;

// --- Patterns ---

const ABSOLUTE_PATH_PATTERN = /^\/|^[A-Za-z]:[\\\/]/;
const TRAVERSAL_PATTERN = /\.\./;

// --- Types ---

export interface ValidationResult {
  valid: boolean;
  error?: string;
}

export interface DefinitionFormErrors {
  name?: string;
  path?: string;
  description?: string;
  playbookType?: string;
}

// --- Valid PlaybookType values ---

const VALID_PLAYBOOK_TYPES: PlaybookType[] = [
  'DEPLOY',
  'CONFIGURATION',
  'RUN_DB_SCRIPT',
  'SERVICE_ACTION',
  'SERVICE_STATUS',
  'UTILITY',
  'SYSTEM_BUILD',
  'SERVER_UP',
  'REBOOT',
  'NONE',
];

// --- Validation Functions ---

/**
 * Validates a playbook definition name.
 * - Required (non-empty, non-whitespace-only)
 * - Maximum 256 characters
 */
export function validateDefinitionName(name: string): ValidationResult {
  if (!name || name.trim().length === 0) {
    return { valid: false, error: 'Name is required' };
  }
  if (name.length > DEFINITION_NAME_MAX_LENGTH) {
    return { valid: false, error: `Name must be at most ${DEFINITION_NAME_MAX_LENGTH} characters` };
  }
  return { valid: true };
}

/**
 * Validates a playbook definition path.
 * - Required (non-empty, non-whitespace-only)
 * - Maximum 512 characters
 * - Must be a relative path (no absolute paths)
 * - Must not contain traversal sequences (..)
 */
export function validateDefinitionPath(path: string): ValidationResult {
  if (!path || path.trim().length === 0) {
    return { valid: false, error: 'Path is required' };
  }
  if (path.length > DEFINITION_PATH_MAX_LENGTH) {
    return { valid: false, error: `Path must be at most ${DEFINITION_PATH_MAX_LENGTH} characters` };
  }
  if (ABSOLUTE_PATH_PATTERN.test(path)) {
    return { valid: false, error: 'Path must be relative without traversal' };
  }
  if (TRAVERSAL_PATTERN.test(path)) {
    return { valid: false, error: 'Path must be relative without traversal' };
  }
  return { valid: true };
}

/**
 * Validates a playbook definition description.
 * - Optional (empty string is valid)
 * - Maximum 1024 characters
 */
export function validateDefinitionDescription(description: string): ValidationResult {
  if (description.length > DEFINITION_DESCRIPTION_MAX_LENGTH) {
    return { valid: false, error: `Description must be at most ${DEFINITION_DESCRIPTION_MAX_LENGTH} characters` };
  }
  return { valid: true };
}

/**
 * Validates all fields of the definition form and returns
 * an object with per-field error messages. An empty object
 * indicates no validation errors.
 */
export function validateDefinitionForm(
  data: { name: string; path: string; description: string; playbookType: string }
): DefinitionFormErrors {
  const errors: DefinitionFormErrors = {};

  const nameResult = validateDefinitionName(data.name);
  if (!nameResult.valid) {
    errors.name = nameResult.error;
  }

  const pathResult = validateDefinitionPath(data.path);
  if (!pathResult.valid) {
    errors.path = pathResult.error;
  }

  const descResult = validateDefinitionDescription(data.description);
  if (!descResult.valid) {
    errors.description = descResult.error;
  }

  if (!data.playbookType || !VALID_PLAYBOOK_TYPES.includes(data.playbookType as PlaybookType)) {
    errors.playbookType = 'Playbook type is required';
  }

  return errors;
}
